import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { setModal } from "../../redux/rightModalSlice";
import { reorderProductCategories, getProductCategoriesTree, getProductCategoriesList, deleteProductCategory } from "../../redux/productCategory/productCategorySlice";
import { moveProductToCategory } from "../../redux/products/productsSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";
import { getNodeApiUrl } from "../../config/api.config";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableTreeNode({ category, level = 0, onReorderChildren, isLast = false, onDeleteRequest, productsRefreshKey, draggedProduct, setDraggedProduct, onProductDrop }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const [isExpanded, setIsExpanded] = useState(level < 1);
    const hasChildren = category.children && category.children.length > 0;
    const hasProducts = category.productCount > 0;
    const isExpandable = hasChildren || hasProducts;
    const isChild = level > 0;

    // Stato per lazy loading prodotti
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [productsLoaded, setProductsLoaded] = useState(false);

    // Stato per drop target highlight
    const [isDragOver, setIsDragOver] = useState(false);
    const dragOverRef = useRef(null);

    // Ricarica i prodotti quando productsRefreshKey cambia (dopo modifica prodotto)
    useEffect(() => {
        if (productsRefreshKey > 0 && productsLoaded && isExpanded) {
            fetchProducts(true);
        }
    }, [productsRefreshKey]);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: category._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto'
    };

    const handleView = (e) => {
        e.stopPropagation();
        dispatch(setModal({ mode: 'view', id: category._id, percentuageWidth: 40 }));
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        dispatch(setModal({ mode: 'edit', id: category._id, percentuageWidth: 40 }));
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        onDeleteRequest(category);
    };

    const handleAddChild = (e) => {
        e.stopPropagation();
        dispatch(setModal({ mode: 'add', id: null, data: { parentId: category._id }, percentuageWidth: 40 }));
    };

    // Conta ricorsivamente le sottocategorie nel tree
    const countChildren = (cat) => {
        if (!cat.children || cat.children.length === 0) return 0;
        let count = cat.children.length;
        cat.children.forEach(c => { count += countChildren(c); });
        return count;
    };

    // Fetch prodotti al primo expand (force=true per ricaricare dopo modifica)
    const fetchProducts = async (force = false) => {
        if (!force && productsLoaded) return;
        if (!accountData?._id) return;
        setProductsLoading(true);
        try {
            const token = localStorage.getItem('installamelo-auth-token');
            const res = await fetch(
                getNodeApiUrl(`/products/categoryProducts?accountId=${accountData._id}&categoryId=${category._id}`),
                { headers: { 'auth-token': token } }
            );
            const data = await res.json();
            setProducts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Errore caricamento prodotti categoria:', err);
            setProducts([]);
        } finally {
            setProductsLoading(false);
            setProductsLoaded(true);
        }
    };

    const handleToggleExpand = (e) => {
        e.stopPropagation();
        const nextExpanded = !isExpanded;
        setIsExpanded(nextExpanded);
        if (nextExpanded && hasProducts && !productsLoaded) {
            fetchProducts();
        }
    };

    // --- HTML5 Drop target sulla riga categoria ---
    const handleDragOver = (e) => {
        if (!draggedProduct) return;
        // Non accettare drop sulla stessa categoria sorgente
        if (draggedProduct.sourceCategoryId === category._id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        // Evita flicker: controlla se il relatedTarget e' ancora dentro il nodo
        if (dragOverRef.current && dragOverRef.current.contains(e.relatedTarget)) return;
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (!draggedProduct || draggedProduct.sourceCategoryId === category._id) return;
        onProductDrop(category);
    };

    // --- HTML5 Drag source sui prodotti ---
    const handleProductDragStart = (e, product) => {
        setDraggedProduct({
            productId: product._id,
            productTitle: product.title || product.code || 'Prodotto',
            sourceCategoryId: category._id
        });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', product._id);
    };

    const handleProductDragEnd = () => {
        setDraggedProduct(null);
    };

    // Bordo verde tratteggiato se si sta trascinando un prodotto sulla categoria
    const dropHighlightClass = isDragOver ? 'ring-2 ring-green-400 ring-dashed bg-green-50/60' : '';

    return (
        <div ref={setNodeRef} style={style}>
            {/* Riga nodo — drop target per prodotti */}
            <div
                ref={dragOverRef}
                className={`flex items-center justify-between py-2.5 px-3 cursor-pointer transition-colors
                    ${isChild
                        ? 'hover:bg-blue-50/60 border-b border-gray-100/80'
                        : 'hover:bg-gray-50 border-b border-gray-200'
                    }
                    ${isDragging ? 'bg-blue-50 shadow-sm' : ''}
                    ${dropHighlightClass}
                `}
                onClick={handleView}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* Drag handle (dnd-kit per riordino categorie) */}
                    <button
                        className="p-1 hover:bg-gray-200 rounded text-gray-400 cursor-grab active:cursor-grabbing flex-shrink-0"
                        {...attributes}
                        {...listeners}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DynamicIcon iconClass="fa-solid fa-grip-vertical text-xs" />
                    </button>

                    {/* Indentazione + connettore per figli */}
                    {isChild && (
                        <div className="flex items-center flex-shrink-0" style={{ width: `${level * 28}px` }}>
                            {Array.from({ length: level }).map((_, i) => (
                                <div key={i} className="flex items-center justify-center" style={{ width: '28px' }}>
                                    {i === level - 1 ? (
                                        <span className="text-gray-300 text-sm font-mono">
                                            {isLast ? '\u2514' : '\u251C'}
                                        </span>
                                    ) : (
                                        <span className="text-gray-200 text-sm font-mono">{'\u2502'}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Freccia espandi/comprimi */}
                    {isExpandable ? (
                        <button
                            className="p-1 hover:bg-gray-200 rounded text-gray-500 flex-shrink-0"
                            onClick={handleToggleExpand}
                        >
                            <DynamicIcon iconClass={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} text-xs`} />
                        </button>
                    ) : (
                        <span className="w-6 flex-shrink-0" />
                    )}

                    {/* Icona categoria */}
                    <DynamicIcon iconClass={`fa-solid ${isChild ? 'fa-folder-open text-yellow-400' : 'fa-folder text-yellow-500'} ${isChild ? 'text-sm' : ''}`} />

                    {/* Nome categoria */}
                    <span className={`truncate ${isChild ? 'text-gray-700 text-sm' : 'font-semibold text-gray-800'}`}>
                        {category.name}
                    </span>

                    {/* Badge conteggio figli */}
                    {hasChildren && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
                            {category.children.length} {category.children.length === 1 ? 'sotto' : 'sotto'}
                        </span>
                    )}

                    {/* Badge conteggio prodotti cumulativo */}
                    {category.cumulativeProductCount > 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex-shrink-0 font-medium">
                            {category.cumulativeProductCount} {category.cumulativeProductCount === 1 ? 'prodotto' : 'prodotti'}
                        </span>
                    )}

                    {/* Badge inattiva */}
                    {category.active === false && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex-shrink-0">
                            Inattiva
                        </span>
                    )}

                    {/* Indicatore drop target durante drag */}
                    {isDragOver && draggedProduct && (
                        <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full flex-shrink-0 font-medium animate-pulse">
                            Rilascia qui
                        </span>
                    )}
                </div>

                {/* Azioni */}
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <button
                        className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                        onClick={handleAddChild}
                        title="Aggiungi sottocategoria"
                    >
                        <DynamicIcon iconClass="fa-solid fa-plus text-xs" />
                    </button>
                    <button
                        className="p-1.5 hover:bg-gray-200 rounded text-gray-600"
                        onClick={handleEdit}
                        title="Modifica"
                    >
                        <DynamicIcon iconClass="fa-solid fa-pen text-xs" />
                    </button>
                    <button
                        className="p-1.5 hover:bg-red-100 rounded text-red-500"
                        onClick={handleDelete}
                        title="Elimina"
                    >
                        <DynamicIcon iconClass="fa-solid fa-trash text-xs" />
                    </button>
                </div>
            </div>

            {/* Contenuto espanso: prodotti inline + figli ricorsivi */}
            {isExpanded && isExpandable && (
                <div className={`${level === 0 ? 'bg-gray-50/50 border-l-4 border-blue-200 ml-4 rounded-bl-lg' : ''}`}>
                    {/* Lista prodotti associati direttamente a questa categoria */}
                    {hasProducts && (
                        <div className="bg-amber-50/60 border-b border-amber-200/50">
                            {productsLoading ? (
                                <div className="flex items-center gap-2 px-4 py-2 text-xs text-amber-600">
                                    <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                    Caricamento prodotti...
                                </div>
                            ) : productsLoaded && products.length > 0 ? (
                                <div className="px-4 py-1.5">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="text-amber-700/70 border-b border-amber-200/40">
                                                <th className="w-6"></th>
                                                <th className="text-left py-1 pr-3 font-medium">Codice</th>
                                                <th className="text-left py-1 pr-3 font-medium">Nome</th>
                                                <th className="text-left py-1 pr-3 font-medium">Matricola</th>
                                                <th className="text-left py-1 pr-3 font-medium">Fornitore</th>
                                                <th className="text-right py-1 pr-3 font-medium">Qta</th>
                                                <th className="w-8"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.map(p => (
                                                <tr
                                                    key={p._id}
                                                    draggable
                                                    onDragStart={(e) => handleProductDragStart(e, p)}
                                                    onDragEnd={handleProductDragEnd}
                                                    className={`border-b border-amber-100/50 hover:bg-amber-100/40 transition-colors cursor-grab active:cursor-grabbing ${draggedProduct?.productId === p._id ? 'opacity-50' : ''}`}
                                                >
                                                    <td className="py-1 text-center text-gray-400">
                                                        <DynamicIcon iconClass="fa-solid fa-grip-vertical text-[10px]" />
                                                    </td>
                                                    <td className="py-1 pr-3 font-mono text-amber-800">{p.code || '-'}</td>
                                                    <td className="py-1 pr-3 text-gray-700 truncate max-w-[200px]">{p.title || '-'}</td>
                                                    <td className="py-1 pr-3 text-gray-500 font-mono">{p.serialNumber || '-'}</td>
                                                    <td className="py-1 pr-3 text-gray-500">{p.supplierLabel || '-'}</td>
                                                    <td className="py-1 text-right pr-3 text-gray-700">{p.quantity ?? '-'}</td>
                                                    <td className="py-1 text-center">
                                                        <button
                                                            className="p-1 hover:bg-amber-200/60 rounded text-amber-700 transition-colors"
                                                            title="Modifica prodotto"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                dispatch(setModal({ mode: 'editProduct', id: p._id }));
                                                            }}
                                                        >
                                                            <DynamicIcon iconClass="fa-solid fa-pen text-xs" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : productsLoaded && products.length === 0 ? (
                                <div className="px-4 py-2 text-xs text-amber-500 italic">
                                    Nessun prodotto trovato
                                </div>
                            ) : null}
                        </div>
                    )}

                    {/* Figli ricorsivi (sottocategorie) */}
                    {hasChildren && (
                        <SortableChildrenList
                            parentId={category._id}
                            children={category.children}
                            level={level + 1}
                            onReorderChildren={onReorderChildren}
                            onDeleteRequest={onDeleteRequest}
                            productsRefreshKey={productsRefreshKey}
                            draggedProduct={draggedProduct}
                            setDraggedProduct={setDraggedProduct}
                            onProductDrop={onProductDrop}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

function SortableChildrenList({ parentId, children, level, onReorderChildren, onDeleteRequest, productsRefreshKey, draggedProduct, setDraggedProduct, onProductDrop }) {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const childIds = children.map(c => c._id);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = childIds.indexOf(active.id);
        const newIndex = childIds.indexOf(over.id);
        const reordered = arrayMove(children, oldIndex, newIndex);

        onReorderChildren(parentId, reordered);
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
                <div>
                    {children.map((child, index) => (
                        <SortableTreeNode
                            key={child._id}
                            category={child}
                            level={level}
                            onReorderChildren={onReorderChildren}
                            onDeleteRequest={onDeleteRequest}
                            isLast={index === children.length - 1}
                            productsRefreshKey={productsRefreshKey}
                            draggedProduct={draggedProduct}
                            setDraggedProduct={setDraggedProduct}
                            onProductDrop={onProductDrop}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}

export default function CategoryTree({ categories, productsRefreshKey = 0, onProductMoved }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');

    // Stato drag & drop prodotti (HTML5 nativo)
    const [draggedProduct, setDraggedProduct] = useState(null);
    const [movingProduct, setMovingProduct] = useState(false);
    const [moveToast, setMoveToast] = useState(null);

    // Stato per il dialog di eliminazione
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteOption, setDeleteOption] = useState('categoryOnly');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Auto-dismiss del toast dopo 3 secondi
    useEffect(() => {
        if (!moveToast) return;
        const timer = setTimeout(() => setMoveToast(null), 3000);
        return () => clearTimeout(timer);
    }, [moveToast]);

    // Gestione drop di un prodotto su una categoria
    const handleProductDrop = useCallback(async (targetCategory) => {
        if (!draggedProduct || !accountData?._id) return;

        setMovingProduct(true);
        setDraggedProduct(null);

        try {
            const result = await dispatch(moveProductToCategory({
                productId: draggedProduct.productId,
                sourceCategoryId: draggedProduct.sourceCategoryId,
                targetCategoryId: targetCategory._id,
                targetCategoryName: targetCategory.name,
                token
            })).unwrap();

            setMoveToast({
                success: true,
                message: `"${draggedProduct.productTitle}" spostato in "${targetCategory.name}"`
            });

            // Refresh albero categorie e prodotti
            dispatch(getProductCategoriesTree({ accountId: accountData._id, token }));
            if (onProductMoved) onProductMoved();
        } catch (err) {
            setMoveToast({
                success: false,
                message: `Errore: ${typeof err === 'string' ? err : 'spostamento fallito'}`
            });
        } finally {
            setMovingProduct(false);
        }
    }, [draggedProduct, accountData, token, dispatch, onProductMoved]);

    if (!categories || categories.length === 0) {
        return (
            <div className="text-center text-gray-500 py-8">
                Nessuna categoria disponibile. Crea la tua prima categoria!
            </div>
        );
    }

    const buildReorderPayload = (nodes, parentId) => {
        return nodes.map((node, index) => ({
            _id: node._id,
            order: index,
            parentId: parentId || null
        }));
    };

    const dispatchReorder = (payload) => {
        dispatch(reorderProductCategories({ categories: payload, token })).then((result) => {
            if (!result.error && accountData?._id) {
                dispatch(getProductCategoriesTree({ accountId: accountData._id, token }));
            }
        });
    };

    const rootIds = categories.map(c => c._id);

    const handleRootDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = rootIds.indexOf(active.id);
        const newIndex = rootIds.indexOf(over.id);
        const reordered = arrayMove(categories, oldIndex, newIndex);

        const payload = buildReorderPayload(reordered, null);
        dispatchReorder(payload);
    };

    const handleReorderChildren = (parentId, reorderedChildren) => {
        const payload = buildReorderPayload(reorderedChildren, parentId);
        dispatchReorder(payload);
    };

    // Conta ricorsivamente le sottocategorie dal tree
    const countAllSubcategories = (cat) => {
        if (!cat.children || cat.children.length === 0) return 0;
        let count = cat.children.length;
        cat.children.forEach(c => { count += countAllSubcategories(c); });
        return count;
    };

    const handleDeleteRequest = (category) => {
        setDeleteTarget(category);
        setDeleteOption('categoryOnly');
        setDeleteError(null);
    };

    const handleDeleteClose = () => {
        setDeleteTarget(null);
        setDeleteOption('categoryOnly');
        setDeleteError(null);
    };

    const handleDeleteConfirm = () => {
        if (!deleteTarget?._id) return;
        setDeleteLoading(true);
        setDeleteError(null);

        dispatch(deleteProductCategory({
            categoryId: deleteTarget._id,
            deleteProducts: deleteOption === 'withProducts',
            token
        })).then((result) => {
            setDeleteLoading(false);
            if (!result.error) {
                setDeleteTarget(null);
                if (accountData?._id) {
                    dispatch(getProductCategoriesTree({ accountId: accountData._id, token }));
                    dispatch(getProductCategoriesList({ accountId: accountData._id, token }));
                }
            } else {
                setDeleteError(result.payload || 'Errore durante l\'eliminazione');
            }
        });
    };

    const totalSubcategories = deleteTarget ? countAllSubcategories(deleteTarget) : 0;

    return (
        <>
            {/* Overlay loading durante spostamento prodotto */}
            {movingProduct && <LoadingSection isFixed />}

            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRootDragEnd}>
                    <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
                        {categories.map((category) => (
                            <SortableTreeNode
                                key={category._id}
                                category={category}
                                onReorderChildren={handleReorderChildren}
                                onDeleteRequest={handleDeleteRequest}
                                productsRefreshKey={productsRefreshKey}
                                draggedProduct={draggedProduct}
                                setDraggedProduct={setDraggedProduct}
                                onProductDrop={handleProductDrop}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>

            {/* Toast feedback spostamento prodotto */}
            {moveToast && (
                <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 transition-all animate-fade-in ${moveToast.success ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                    <DynamicIcon iconClass={moveToast.success ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'} />
                    {moveToast.message}
                    <button
                        className="ml-2 hover:opacity-80"
                        onClick={() => setMoveToast(null)}
                    >
                        <DynamicIcon iconClass="fas fa-times text-xs" />
                    </button>
                </div>
            )}

            {/* Dialog conferma eliminazione */}
            {deleteTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    {deleteLoading && <LoadingSection isFixed />}
                    <div className="bg-white rounded-xl p-5 max-w-md w-full shadow-2xl">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                <DynamicIcon iconClass="fas fa-exclamation-triangle text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Elimina categoria</h3>
                                <p className="text-xs text-gray-500">Azione irreversibile</p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            Stai per eliminare <span className="font-semibold">"{deleteTarget.name}"</span>
                            {totalSubcategories > 0 && (
                                <> e le sue <span className="font-semibold">{totalSubcategories} sottocategorie</span></>
                            )}.
                            Scegli cosa fare con i prodotti associati:
                        </p>

                        {/* Opzioni di eliminazione */}
                        <div className="space-y-2 mb-4">
                            <label
                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${deleteOption === 'categoryOnly' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                                onClick={() => setDeleteOption('categoryOnly')}
                            >
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="categoryOnly"
                                    checked={deleteOption === 'categoryOnly'}
                                    onChange={() => setDeleteOption('categoryOnly')}
                                    className="mt-0.5"
                                />
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Solo categoria{totalSubcategories > 0 ? ' e sottocategorie' : ''}</p>
                                    <p className="text-xs text-gray-500">I prodotti associati non verranno eliminati, resteranno senza categoria</p>
                                </div>
                            </label>

                            <label
                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${deleteOption === 'withProducts' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                                onClick={() => setDeleteOption('withProducts')}
                            >
                                <input
                                    type="radio"
                                    name="deleteOption"
                                    value="withProducts"
                                    checked={deleteOption === 'withProducts'}
                                    onChange={() => setDeleteOption('withProducts')}
                                    className="mt-0.5"
                                />
                                <div>
                                    <p className="text-sm font-semibold text-red-700">Categoria{totalSubcategories > 0 ? ', sottocategorie' : ''} e prodotti</p>
                                    <p className="text-xs text-gray-500">Verranno eliminati anche tutti i prodotti associati alla categoria{totalSubcategories > 0 ? ' e alle sottocategorie' : ''}</p>
                                </div>
                            </label>
                        </div>

                        {deleteError && (
                            <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
                                {typeof deleteError === 'object' ? deleteError.message || JSON.stringify(deleteError) : deleteError}
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <button
                                className="secondary"
                                onClick={handleDeleteClose}
                                disabled={deleteLoading}
                            >
                                Annulla
                            </button>
                            <button
                                className="danger"
                                onClick={handleDeleteConfirm}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <><DynamicIcon iconClass="fas fa-spinner fa-spin" /> Eliminazione...</>
                                ) : (
                                    <><DynamicIcon iconClass="fas fa-trash" /> Elimina</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
